import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect } from "react";
import { useForm, SubmitHandler, Controller, Resolver } from "react-hook-form";
import {
  Button,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { CalendarInput } from "../../../../../components/core/CalendarInput/CalendarInput";
import { FormField } from "../../../../../components/core/FormField/FormField";
import { Loader } from "../../../../../components/core/Loader/Loader";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import TextInput from "../../../../../components/core/TextInput/TextInput";
import { FormSection } from "../../../../../components/FormSection/FormSection";
import {
  NAME_LABEL,
  IS_REQUIRED_LABEL,
  SURNAME_LABEL,
  FISCAL_CODE_LABEL,
  IS_NOT_VALID_LABEL,
  EMAIL_LABEL,
  COLLABORATION_START_DATE,
  COLLABORATION_END_DATE,
  ADD_EXTERNAL_COLLABORATOR,
  SAVE_LABEL,
  CANCEL_LABEL,
  PERSONAL_DATA,
  CONTRACT,
  COMPANY_DATA,
  PRACTICE_LABEL,
  CC_LABEL,
  EDIT_EXTERNAL_COLLABORATOR,
  INVALID_DATE_LABEL,
  MUST_BE_AFTER_THAN_LABEL,
} from "../../../../../constants/Dictionary";
import { StyledConfigExternalCollaboratorModal } from "./ConfigExternalCollaboratorModal.styled";
import * as yup from "yup";
import { Modal } from "../../../../../components/core/Modal/Modal";
import { Divider } from "../../../../../components/core/Divider/Divider";
import { ReactComponent as UserBoxIcon } from "../../../../../icons/userBox.svg";
import { ReactComponent as ContractIcon } from "../../../../../icons/details.svg";
import { ReactComponent as CompanyIcon } from "../../../../../icons/company.svg";
import { ReactComponent as CancelIcon } from "../../../../../icons/close.svg";
import { ReactComponent as SaveIcon } from "../../../../../icons/save.svg";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { OrgCCItem, OrgPracticeItem } from "../../../../../types/api/org.type";
import {
  AddConfigExternalCollaboratorRequest,
  ConfigCollaborator,
  UpdateConfigExternalCollaboratorRequest,
} from "../../../../../types/api/configurations.type";
import dayjs from "dayjs";
import { EN_DATE_FORMAT } from "../../../../../utils/date/date.utils";

type FormData = {
  firstName: string;
  lastName: string;
  taxIdCode: string;
  email: string;
  startDate: Date | null;
  endDate?: Date | null;
  practice: string | null;
  cc: string | null;
};

type ConfigExternalCollaboratorModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  collaborator?: ConfigCollaborator;
  onSave: (
    collaboratorData:
      | UpdateConfigExternalCollaboratorRequest
      | AddConfigExternalCollaboratorRequest,
  ) => void;
  loading: boolean;
};

export const ConfigExternalCollaboratorModal = ({
  isOpen,
  closeModal,
  collaborator,
  onSave,
  loading,
}: ConfigExternalCollaboratorModalProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);
  const area = orgSettings.areaList.all[0];
  const [practiceList, setPracticeList] = useState<OrgPracticeItem[]>(
    area.practiceList,
  );
  const [ccList, setCCList] = useState<OrgCCItem[]>([]);

  const schema = yup
    .object({
      firstName: yup.string().required(`${NAME_LABEL} ${IS_REQUIRED_LABEL}`),
      lastName: yup.string().required(`${SURNAME_LABEL} ${IS_REQUIRED_LABEL}`),
      taxIdCode: yup
        .string()
        .matches(
          /^$|^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/,
          `${FISCAL_CODE_LABEL} ${IS_NOT_VALID_LABEL}`,
        )
        .required(`${FISCAL_CODE_LABEL} ${IS_REQUIRED_LABEL}`),
      email: yup
        .string()
        .email(`${EMAIL_LABEL} ${IS_NOT_VALID_LABEL}`)
        .required(`${EMAIL_LABEL} ${IS_REQUIRED_LABEL}`),
      startDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .required(`${COLLABORATION_START_DATE} ${IS_REQUIRED_LABEL}`),
      endDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .test(
          "is-after-startDate",
          `La ${COLLABORATION_END_DATE} ${MUST_BE_AFTER_THAN_LABEL} ${COLLABORATION_START_DATE}.`,
          function (endDate) {
            const { startDate } = this.parent;
            if (!endDate || !startDate) {
              return true;
            }
            return endDate.getTime() - startDate.getTime() >= 0;
          },
        ),
      practice: yup
        .string()
        .nullable()
        .required(`${PRACTICE_LABEL} ${IS_REQUIRED_LABEL}`),
      cc: yup.string().nullable().required(`${CC_LABEL} ${IS_REQUIRED_LABEL}`),
    })
    .required();

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
  });

  const selectedPractice = watch("practice");

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    if (collaborator) {
      const body: UpdateConfigExternalCollaboratorRequest = {
        ...(dirtyFields.firstName && { firstName: data.firstName }),
        ...(dirtyFields.lastName && { lastName: data.lastName }),
        ...(dirtyFields.taxIdCode && { taxIdCode: data.taxIdCode }),
        ...(dirtyFields.email && { email: data.email }),
        ...(dirtyFields.startDate && {
          startDate: dayjs(data.startDate).format(EN_DATE_FORMAT),
        }),
        ...(dirtyFields.endDate && {
          endDate: data.endDate
            ? dayjs(data.endDate).format(EN_DATE_FORMAT)
            : null,
        }),
        ...(dirtyFields.practice && { practiceId: data.practice! }),
        ...(dirtyFields.cc && { ccId: data.cc! }),
      };
      onSave(body);
    } else {
      const body: AddConfigExternalCollaboratorRequest = {
        firstName: data.firstName,
        lastName: data.lastName,
        taxIdCode: data.taxIdCode,
        email: data.email,
        startDate: dayjs(data.startDate).format(EN_DATE_FORMAT),
        endDate: data.endDate
          ? dayjs(data.endDate).format(EN_DATE_FORMAT)
          : null,
        areaId: area.id,
        practiceId: data.practice!,
        ccId: data.cc!,
      };
      onSave(body);
    }
  };

  const setPracticeOptions = () => {
    const areaPractice = area.practiceList;
    setPracticeList(areaPractice);

    if (collaborator?.practiceId) {
      const practice = areaPractice.find(
        (p) => p.id === collaborator.practiceId,
      );
      setCCList(practice ? practice.ccList : []);
    }
  };

  const setCCOptions = (practiceId: string) => {
    const practice = practiceList.find(
      (practice) => practice.id === practiceId,
    );
    setCCList(practice ? practice.ccList : []);
  };

  useEffect(() => {
    if (collaborator) {
      reset({
        firstName: collaborator.firstName,
        lastName: collaborator.lastName,
        email: collaborator.email,
        taxIdCode: collaborator.taxIdCode,
        startDate: collaborator.startDate
          ? dayjs(collaborator.startDate).toDate()
          : null,
        endDate: collaborator.endDate
          ? dayjs(collaborator.endDate).toDate()
          : null,
        practice: collaborator.practiceId ?? null,
        cc: collaborator.ccId ?? null,
      });
      setPracticeOptions();
    } else {
      reset({
        firstName: "",
        lastName: "",
        email: "",
        taxIdCode: "",
        startDate: null,
        endDate: null,
        practice: null,
        cc: null,
      });
    }
  }, [collaborator, isOpen]);

  const startDate = watch("startDate");

  return (
    <Modal
      fullSize={false}
      label={
        collaborator ? EDIT_EXTERNAL_COLLABORATOR : ADD_EXTERNAL_COLLABORATOR
      }
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<SaveIcon />}
          label={SAVE_LABEL}
          disabled={!isDirty || loading}
          type="submit"
          form="collaboratorAdd"
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={closeModal}
        />
      }
    >
      <Loader loading={loading}>
        <StyledConfigExternalCollaboratorModal>
          <form
            id="collaboratorAdd"
            onSubmit={handleSubmit(onSubmit)}
            className="collaboratorAdd"
          >
            <FormSection label={PERSONAL_DATA} icon={<UserBoxIcon />}>
              <div className="form-row">
                <FormField error={errors.firstName?.message}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={NAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.lastName?.message}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={SURNAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className="form-row">
                <FormField error={errors.taxIdCode?.message}>
                  <Controller
                    name="taxIdCode"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={FISCAL_CODE_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.email?.message}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={EMAIL_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
            <Divider />
            <FormSection label={CONTRACT} icon={<ContractIcon />}>
              <div className="form-row">
                <FormField error={errors.startDate?.message}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <CalendarInput
                        label={COLLABORATION_START_DATE}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.endDate?.message}>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <CalendarInput
                        label={COLLABORATION_END_DATE}
                        minDate={
                          startDate && !isNaN(startDate.getTime())
                            ? startDate
                            : undefined
                        }
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
            <Divider />
            <FormSection label={COMPANY_DATA} icon={<CompanyIcon />}>
              <div className="form-row">
                <FormField error={errors.practice?.message}>
                  <Controller
                    name="practice"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={PRACTICE_LABEL}
                        options={practiceList.map((practice) => ({
                          value: practice.id,
                          label: practice.name,
                        }))}
                        onChange={(value) => {
                          onChange(value);
                          if (value) {
                            setCCOptions(value);
                          }
                          setValue("cc", null, { shouldDirty: true });
                        }}
                        value={value}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.cc?.message}>
                  <Controller
                    name="cc"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={CC_LABEL}
                        options={ccList.map((cc) => ({
                          value: cc.id,
                          label: cc.name,
                        }))}
                        disabled={!selectedPractice}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
          </form>
        </StyledConfigExternalCollaboratorModal>
      </Loader>
    </Modal>
  );
};
